import React from 'react';
import { ChevronRight, ExternalLink, X } from 'lucide-react';

export interface FacilityData {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  sector: string;
  region?: string | null;
  state?: string | null;
  lga?: string | null;
  subSector?: string | null;
  oilBlock?: string | null;
  oilfield?: string | null;
  operator?: string | null;
  facilityType?: string | null;
  geographicLocation?: string | null;
  customField1?: string | null;
  customField2?: string | null;
  customField3?: string | null;
  isSatellite?: boolean;
  /** When `isSatellite`, which upstream feed normalized this point (`carbon_mapper` | `imeo` | `tropomi`). */
  satelliteProvider?: string;
  emissionRate?: number;
  emissionUncertainty?: number;
  plumeCount?: number;
  persistence?: number;
  instrument?: string;
  firstDetected?: string;
  lastDetected?: string;
  /** IMEO plume image URL when provider is `imeo`. */
  plumeImageUrl?: string;
}

interface FacilityPopupProps {
  darkMode: boolean;
  facility: FacilityData;
  onExpand: () => void;
  onClose: () => void;
}

const FacilityPopup: React.FC<FacilityPopupProps> = ({ darkMode, facility, onExpand, onClose }) => (
  <div className={`fixed bottom-16 left-0 right-0 mx-auto w-[92%] md:absolute md:bottom-auto md:left-[55%] md:right-auto md:top-1/2 md:-translate-y-1/2 md:ml-4 md:w-72 md:mx-0 rounded-3xl shadow-2xl p-6 border z-50 transition-colors animate-in fade-in slide-in-from-bottom-4 md:slide-in-from-right-4 duration-300 ${darkMode ? 'bg-[#12161f] border-[#1e2430]' : 'bg-white border-gray-100'}`}>
    <button onClick={onClose} className={`absolute top-3 right-3 p-1.5 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}>
      <X size={16} />
    </button>

    <h3 className={`font-bold text-base break-all pr-6 leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>{facility.name}</h3>

    <div className="flex items-center gap-2 mt-2">
      {facility.isSatellite ? (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
          facility.satelliteProvider === 'imeo'
            ? 'bg-cyan-500/15 text-cyan-400'
            : facility.satelliteProvider === 'tropomi'
              ? 'bg-violet-500/15 text-violet-300'
              : 'bg-orange-500/15 text-orange-400'
        }`}>
          {facility.satelliteProvider === 'imeo'
            ? 'IMEO (UNEP)'
            : facility.satelliteProvider === 'tropomi'
              ? 'TROPOMI'
              : 'Carbon Mapper'}
        </span>
      ) : (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-500/15 text-teal-400">Facility</span>
      )}
      {(facility.plumeCount ?? 0) > 0 && (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${facility.isSatellite ? 'bg-orange-500/10 text-orange-300' : 'bg-teal-500/10 text-teal-300'}`}>
          {facility.plumeCount} {facility.isSatellite ? 'plume' : 'measurement'}{(facility.plumeCount ?? 0) > 1 ? 's' : ''}
        </span>
      )}
    </div>

    <a
      href={`https://www.google.com/maps?q=${facility.latitude},${facility.longitude}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-teal-500 text-xs font-bold mt-2 flex items-center gap-1 hover:underline"
    >
      Open in Google Maps <ExternalLink size={12} />
    </a>

    <div className="mt-4 space-y-2.5 text-sm">
      <PopupRow darkMode={darkMode} label="Sector" value={facility.sector} />
      {!facility.isSatellite && facility.subSector && <PopupRow darkMode={darkMode} label="Sub-sector" value={facility.subSector} />}
      {facility.region && <PopupRow darkMode={darkMode} label="Region" value={facility.region} />}
      {!facility.isSatellite && facility.facilityType && <PopupRow darkMode={darkMode} label="Facility Type" value={facility.facilityType} />}
      {!facility.isSatellite && facility.operator && <PopupRow darkMode={darkMode} label="Operator" value={facility.operator} />}
      {facility.oilBlock && <PopupRow darkMode={darkMode} label="Oil Block" value={facility.oilBlock} />}
      {facility.oilfield && <PopupRow darkMode={darkMode} label="Oilfield" value={facility.oilfield} />}
      {facility.state && <PopupRow darkMode={darkMode} label="State" value={facility.state} />}
      {facility.lga && <PopupRow darkMode={darkMode} label="LGA" value={facility.lga} />}
      {!facility.isSatellite && facility.geographicLocation && <PopupRow darkMode={darkMode} label="Location" value={facility.geographicLocation} />}
      {!facility.isSatellite && facility.customField1 && <PopupRow darkMode={darkMode} label="Custom Field 1" value={facility.customField1} />}
      {!facility.isSatellite && facility.customField2 && <PopupRow darkMode={darkMode} label="Custom Field 2" value={facility.customField2} />}
      {!facility.isSatellite && facility.customField3 && <PopupRow darkMode={darkMode} label="Custom Field 3" value={facility.customField3} />}
      <PopupRow darkMode={darkMode} label="Gas Type" value="CH₄" />
      {facility.isSatellite && (
        <PopupRow
          darkMode={darkMode}
          label="Emission Rate"
          value={facility.emissionRate && facility.emissionRate > 0
            ? `${facility.emissionRate.toFixed(1)} kg/hr`
            : 'Detected (rate pending)'}
        />
      )}
      {facility.isSatellite && facility.emissionUncertainty != null && facility.emissionUncertainty > 0 && (
        <PopupRow darkMode={darkMode} label="Uncertainty" value={`± ${facility.emissionUncertainty.toFixed(1)} kg/hr`} />
      )}
      {facility.isSatellite && (
        <PopupRow darkMode={darkMode} label="Plume Count" value={String(facility.plumeCount ?? 0)} />
      )}
      {facility.isSatellite && (facility.persistence ?? 0) > 0 && (
        <PopupRow darkMode={darkMode} label="Persistence" value={`${((facility.persistence ?? 0) * 100).toFixed(0)}%`} />
      )}
      {facility.isSatellite && facility.instrument && (
        <PopupRow darkMode={darkMode} label="Instrument" value={facility.instrument} />
      )}
      {facility.isSatellite && facility.lastDetected && (
        <PopupRow darkMode={darkMode} label="Last Detected" value={new Date(facility.lastDetected).toLocaleDateString()} border={false} />
      )}
      {!facility.isSatellite && (facility.plumeCount ?? 0) > 0 && (
        <PopupRow darkMode={darkMode} label="Measurements" value={String(facility.plumeCount)} border={false} />
      )}
    </div>

    <button
      onClick={onExpand}
      className="nogiet-button nogiet-button-primary w-full mt-5 flex"
    >
      View Full Details <ChevronRight size={16} />
    </button>
  </div>
);

const PopupRow: React.FC<{ darkMode: boolean; label: string; value: string; border?: boolean }> = ({ darkMode, label, value, border = true }) => (
  <div className={`flex justify-between py-2 ${border ? `border-b ${darkMode ? 'border-[#1e2430]' : 'border-gray-50'}` : ''}`}>
    <span className="text-gray-500">{label}</span>
    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>{value}</span>
  </div>
);

export default FacilityPopup;
